import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apis } from '@/config/apiConfig'
import { apiFetch } from '@/utilities/api'
import { verifyResult } from '@/utilities/utils'

const fetchAsPromise = (url, method = 'GET', body = null) => {
  return new Promise((resolve, reject) => {
    apiFetch(url, method, body, result => {
      if (result && result.success) {
        resolve(result)
      } else {
        const errorMessage =
          result?.message || 'API request failed due to an unknown error.'
        const error = new Error(errorMessage)
        if (result) {
          error.response = result
        } else {
          error.response = { success: false, message: errorMessage }
        }
        reject(error)
      }
    })
  })
}

export const usersKeys = {
  all: ['users'],
  account: accountId => [...usersKeys.all, 'account', accountId],
  roles: ['roles'],
  userImages: userId => ['userImage', userId],
  userImagesList: userIds => {
    const idsArray = Array.isArray(userIds) ? userIds : [userIds]
    return [
      'userImages',
      ...[...idsArray].sort((a, b) => String(a).localeCompare(String(b))),
    ]
  },
}

async function fetchUsers(accountId) {
  try {
    const result = await fetchAsPromise(
      `${apis.apiDatabase.uri}account/users`,
      'POST',
      { accountID: accountId }
    )
    if (!verifyResult(result)) {
      throw new Error(
        result.message ||
          'Failed to fetch users after successful API call but failed verification'
      )
    }
    return result
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch users')
  }
}

async function fetchRoles() {
  try {
    const result = await fetchAsPromise(
      `${apis.apiDatabase.uri}roles`,
      'GET',
      null
    )
    if (!verifyResult(result)) {
      throw new Error(
        result.message ||
          'Failed to fetch roles after successful API call but failed verification'
      )
    }
    return result
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch roles')
  }
}

async function updateUserRole({ accountID, userID, role }) {
  const result = await new Promise((resolve, reject) => {
    apiFetch(
      `${apis.apiDatabase.uri}account/user/role`,
      'POST',
      { accountID, userID, role },
      response => {
        if (response && response.success) {
          resolve(response)
        } else {
          reject(new Error(response?.message || 'Failed to update user role'))
        }
      }
    )
  })
  return result
}

async function fetchUserImage(user, getImage) {
  try {
    const imgURL = await getImage(user.profileImg, 'user.png')
    return {
      id: user.id,
      name: user.displayName,
      image: imgURL,
    }
  } catch (error) {
    console.error(`Error loading image for user ${user.id}:`, error)
    return {
      id: user.id,
      name: user.displayName,
      image: '/client_data/static/images/user.png', // Fallback placeholder
    }
  }
}

export const useUsers = accountId => {
  return useQuery({
    queryKey: usersKeys.account(accountId),
    queryFn: () => fetchUsers(accountId),
    select: result => result.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useRoles = () => {
  return useQuery({
    queryKey: usersKeys.roles,
    queryFn: fetchRoles,
    select: result => result.data,
    staleTime: 1000 * 60 * 60, // 1 hour - roles change less frequently
    refetchOnWindowFocus: false,
  })
}

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateUserRole,
    onMutate: async variables => {
      await queryClient.cancelQueries({
        queryKey: usersKeys.account(variables.accountID),
      })

      const previousUsers = queryClient.getQueryData(
        usersKeys.account(variables.accountID)
      )

      const cachedRolesData = queryClient.getQueryData(usersKeys.roles)
      const roles = cachedRolesData?.data || []
      const matchingRole = roles.find(r => r.slug === variables.role)

      if (previousUsers?.data && matchingRole) {
        queryClient.setQueryData(
          usersKeys.account(variables.accountID),
          old => {
            if (!old) return old

            return {
              ...old,
              data: old.data.map(user => {
                if (user.id === variables.userID) {
                  return {
                    ...user,
                    role: {
                      id: matchingRole.id,
                      slug: matchingRole.slug,
                      name: matchingRole.name,
                      description: matchingRole.description,
                    },
                  }
                }
                return user
              }),
            }
          }
        )
      }

      return { previousUsers }
    },

    onError: (error, variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          usersKeys.account(variables.accountID),
          context.previousUsers
        )
      }
    },

    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: usersKeys.account(variables.accountID),
      })
    },
  })
}

export const useUserImages = (users, getImage) => {
  const queryClient = useQueryClient()
  const sortedUserIds = Array.isArray(users)
    ? [...users.map(u => u.id)].sort((a, b) =>
        String(a).localeCompare(String(b))
      )
    : []

  return useQuery({
    queryKey: usersKeys.userImagesList(sortedUserIds),
    queryFn: async () => {
      if (!users || !users.length) return []

      const processUser = async user => {
        const cachedKey = usersKeys.userImages(user.id)
        const cached = queryClient.getQueryData(cachedKey)
        if (cached) return cached

        const imgData = await fetchUserImage(user, getImage)
        queryClient.setQueryData(cachedKey, imgData)
        return imgData
      }

      const results = await Promise.allSettled(users.map(processUser))

      return results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value
        }
        return {
          id: users[index].id,
          name: users[index].displayName,
          image: '/client_data/static/images/user.png',
        }
      })
    },
    enabled: users && users.length > 0,
    staleTime: 1000 * 60 * 60, // 60 minutes
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  })
}
